import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader } from "@/components/shieldx/Primitives";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import About from "@/pages/About";
import AuthCallback from "@/pages/AuthCallback";
import AppShell from "@/components/shieldx/AppShell";

import CommandCenter from "@/pages/app/CommandCenter";
import Dashboard from "@/pages/app/Dashboard";
import Organizations from "@/pages/app/Organizations";
import Assets from "@/pages/app/Assets";
import AIAgents from "@/pages/app/AIAgents";
import Protection from "@/pages/app/Protection";
import AuthorizationCenter from "@/pages/app/Authorization";
import AIAccess from "@/pages/app/AIAccess";
import Integrations from "@/pages/app/Integrations";
import Threats from "@/pages/app/Threats";
import Incidents from "@/pages/app/Incidents";
import IncidentDetail from "@/pages/app/IncidentDetail";
import Policies from "@/pages/app/Policies";
import SOC from "@/pages/app/SOC";
import DefenseWall from "@/pages/app/DefenseWall";
import Verification from "@/pages/app/Verification";
import BrowserIntegrity from "@/pages/app/BrowserIntegrity";
import Throttling from "@/pages/app/Throttling";
import ZKAuthorization from "@/pages/app/ZKAuthorization";
import ThreatTrap from "@/pages/app/ThreatTrap";
import ShadowAI from "@/pages/app/ShadowAI";
import AuditVault from "@/pages/app/AuditVault";
import AccessGovernance from "@/pages/app/AccessGovernance";
import PlatformSecurity from "@/pages/app/PlatformSecurity";
import Simulation from "@/pages/app/Simulation";
import Scorecard from "@/pages/app/Scorecard";
import AuditLogs from "@/pages/app/AuditLogs";
import Reports from "@/pages/app/Reports";
import Emergency from "@/pages/app/Emergency";
import SettingsPage from "@/pages/app/Settings";
import Consensus from "@/pages/app/Consensus";
import HardwareAuthz from "@/pages/app/HardwareAuthz";
import SideChannel from "@/pages/app/SideChannel";
import MemoryState from "@/pages/app/MemoryState";
import NetworkQuarantine from "@/pages/app/NetworkQuarantine";
import CreatorGuard from "@/pages/app/CreatorGuard";
import Defense from "@/pages/app/Defense";
import Aerospace from "@/pages/app/Aerospace";
import Adversarial from "@/pages/app/Adversarial";
import WebsiteResilience from "@/pages/app/WebsiteResilience";
import LoginGuard from "@/pages/app/LoginGuard";
import QuantumDefense from "@/pages/app/QuantumDefense";
import CommandValidation from "@/pages/app/CommandValidation";
import SelfHealing from "@/pages/app/SelfHealing";
import SecurityPipeline from "@/pages/app/SecurityPipeline";
import RoleDashboard from "@/pages/app/RoleDashboard";
import NotificationCenter from "@/pages/app/NotificationCenter";
import SARegistrations from "@/pages/superadmin/Registrations";

import ErrorBoundary from "@/components/shieldx/ErrorBoundary";
import SAOverview from "@/pages/superadmin/Overview";
import SAOrganizations from "@/pages/superadmin/Organizations";
import SAUsers from "@/pages/superadmin/Users";
import SAAssets from "@/pages/superadmin/Assets";
import SASecurityScreening from "@/pages/superadmin/SecurityScreening";
import SAAudit from "@/pages/superadmin/Audit";
import SAPlatformSettings from "@/pages/superadmin/PlatformSettings";
import SAEmailCenter from "@/pages/superadmin/EmailCenter";
import SAIncidents from "@/pages/superadmin/Incidents";
import SAPolicies from "@/pages/superadmin/Policies";
import ViewAs from "@/pages/superadmin/ViewAs";
import { Building2, ShieldUser, Landmark, ShieldHalf, Satellite } from "lucide-react";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="grid min-h-screen place-items-center bg-slate-950"><Loader label="Verifying session" /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function SuperAdminRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="grid min-h-screen place-items-center bg-slate-950"><Loader label="Authorizing" /></div>;
  if (!user || user.role !== "super_admin") return <Navigate to="/app/command-center" replace />;
  return children;
}

function AppIndexRedirect() {
  const { user } = useAuth();
  if (user && user.role === "super_admin") return <Navigate to="/app/super-admin" replace />;
  return <Navigate to="/app/command-center" replace />;
}

const SPECIALIZED_ROLES = ["soc_lead", "security_analyst", "incident_responder", "auditor", "viewer", "individual", "creator"];
function RoleHome() {
  const { user } = useAuth();
  if (user && SPECIALIZED_ROLES.includes(user.role)) return <RoleDashboard />;
  return <CommandCenter />;
}

function Root() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/about" element={<About />} />
      <Route path="/app" element={<Protected><AppShell /></Protected>}>
        <Route index element={<AppIndexRedirect />} />
        <Route path="super-admin" element={<SuperAdminRoute><ErrorBoundary><SAOverview /></ErrorBoundary></SuperAdminRoute>} />
        <Route path="super-admin/organizations" element={<SuperAdminRoute><ErrorBoundary><SAOrganizations /></ErrorBoundary></SuperAdminRoute>} />
        <Route path="super-admin/creators" element={<SuperAdminRoute><ErrorBoundary><SAOrganizations category="creator" title="Creators & Influencers" icon={ShieldUser} subtitle="Creator and influencer security governance." /></ErrorBoundary></SuperAdminRoute>} />
        <Route path="super-admin/government" element={<SuperAdminRoute><ErrorBoundary><SAOrganizations category="government" title="Government Organizations" icon={Landmark} subtitle="Government digital-service security governance." /></ErrorBoundary></SuperAdminRoute>} />
        <Route path="super-admin/defense" element={<SuperAdminRoute><ErrorBoundary><SAOrganizations category="defense" title="Defense Organizations" icon={ShieldHalf} subtitle="Defense security command governance." /></ErrorBoundary></SuperAdminRoute>} />
        <Route path="super-admin/aerospace" element={<SuperAdminRoute><ErrorBoundary><SAOrganizations category="aerospace" title="Aerospace & Space" icon={Satellite} subtitle="Aerospace mission-system security governance." /></ErrorBoundary></SuperAdminRoute>} />
        <Route path="super-admin/users" element={<SuperAdminRoute><ErrorBoundary><SAUsers /></ErrorBoundary></SuperAdminRoute>} />
        <Route path="super-admin/assets" element={<SuperAdminRoute><ErrorBoundary><SAAssets /></ErrorBoundary></SuperAdminRoute>} />
        <Route path="super-admin/incidents" element={<SuperAdminRoute><ErrorBoundary><SAIncidents /></ErrorBoundary></SuperAdminRoute>} />
        <Route path="super-admin/policies" element={<SuperAdminRoute><ErrorBoundary><SAPolicies /></ErrorBoundary></SuperAdminRoute>} />
        <Route path="super-admin/registrations" element={<SuperAdminRoute><ErrorBoundary><SARegistrations /></ErrorBoundary></SuperAdminRoute>} />
        <Route path="super-admin/view-as/:orgId" element={<SuperAdminRoute><ErrorBoundary><ViewAs /></ErrorBoundary></SuperAdminRoute>} />
        <Route path="super-admin/screening" element={<SuperAdminRoute><ErrorBoundary><SASecurityScreening /></ErrorBoundary></SuperAdminRoute>} />
        <Route path="super-admin/email" element={<SuperAdminRoute><ErrorBoundary><SAEmailCenter /></ErrorBoundary></SuperAdminRoute>} />
        <Route path="super-admin/audit" element={<SuperAdminRoute><ErrorBoundary><SAAudit /></ErrorBoundary></SuperAdminRoute>} />
        <Route path="super-admin/settings" element={<SuperAdminRoute><ErrorBoundary><SAPlatformSettings /></ErrorBoundary></SuperAdminRoute>} />
        <Route path="command-center" element={<RoleHome />} />
        <Route path="notifications" element={<ErrorBoundary><NotificationCenter /></ErrorBoundary>} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="protection" element={<Protection />} />
        <Route path="organizations" element={<Organizations />} />
        <Route path="assets" element={<Assets />} />
        <Route path="authorization" element={<AuthorizationCenter />} />
        <Route path="ai-access" element={<AIAccess />} />
        <Route path="ai-agents" element={<AIAgents />} />
        <Route path="integrations" element={<Integrations />} />
        <Route path="threats" element={<Threats />} />
        <Route path="incidents" element={<Incidents />} />
        <Route path="incidents/:id" element={<IncidentDetail />} />
        <Route path="policies" element={<Policies />} />
        <Route path="soc" element={<SOC />} />
        <Route path="defense-wall" element={<DefenseWall />} />
        <Route path="verification" element={<Verification />} />
        <Route path="browser-integrity" element={<BrowserIntegrity />} />
        <Route path="throttling" element={<Throttling />} />
        <Route path="zk-authorization" element={<ZKAuthorization />} />
        <Route path="threat-trap" element={<ThreatTrap />} />
        <Route path="shadow-ai" element={<ShadowAI />} />
        <Route path="audit-vault" element={<AuditVault />} />
        <Route path="access-governance" element={<AccessGovernance />} />
        <Route path="platform-security" element={<PlatformSecurity />} />
        <Route path="consensus" element={<Consensus />} />
        <Route path="hardware-authz" element={<HardwareAuthz />} />
        <Route path="side-channel" element={<SideChannel />} />
        <Route path="memory-state" element={<MemoryState />} />
        <Route path="network-quarantine" element={<NetworkQuarantine />} />
        <Route path="creator-guard" element={<CreatorGuard />} />
        <Route path="defense" element={<Defense />} />
        <Route path="aerospace" element={<Aerospace />} />
        <Route path="adversarial" element={<Adversarial />} />
        <Route path="website-resilience" element={<WebsiteResilience />} />
        <Route path="login-guard" element={<LoginGuard />} />
        <Route path="quantum-defense" element={<QuantumDefense />} />
        <Route path="command-validation" element={<CommandValidation />} />
        <Route path="security-pipeline" element={<SecurityPipeline />} />
        <Route path="self-healing" element={<SelfHealing />} />
        <Route path="simulation" element={<Simulation />} />
        <Route path="simulation/:id" element={<Scorecard />} />
        <Route path="audit-logs" element={<AuditLogs />} />
        <Route path="reports" element={<Reports />} />
        <Route path="emergency" element={<Emergency />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Root />
          <Toaster position="top-right" theme="dark" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
